import React, { ReactNode } from "react";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Input from "antd/es/input";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { FormattedMessage, useIntl } from "react-intl";

import { authCommonStyles as commonStyles } from "@mapmycustomers/auth";
import { Button, FormItem } from "@mapmycustomers/ui";

import PasswordValidation from "@app/component/auth/PasswordValidation";
import Checkbox from "@app/component/input/Checkbox";
import SignupField from "@app/scene/auth/component/Register/enum/SignupField";
import BaseStepProps from "@app/scene/auth/component/Register/types/BaseStepProps";
import {
  useAcceptAgreementValidation,
  useConfirmPasswordFieldValidationRules,
  usePasswordFieldValidationRules,
} from "@app/scene/auth/component/Register/utils/validation-hooks";
import { PasswordValidationResult } from "@app/util/validatePasswords";

import styles from "../../Steps.module.scss";

interface Props extends BaseStepProps {
  passwordValidationResult: PasswordValidationResult;
  signingUp: boolean;
}

const PasswordStep = ({
  className,
  form,
  isValid,
  onBackClick,
  passwordValidationResult,
  signingUp,
}: Props) => {
  const intl = useIntl();

  const passwordFieldValidationRules = usePasswordFieldValidationRules(intl);
  const confirmPasswordFieldValidationRules = useConfirmPasswordFieldValidationRules(intl, form);
  const tncValidation = useAcceptAgreementValidation(intl);

  return (
    <div className={cn(styles.stepContainer, className)}>
      <p className={commonStyles.description}>
        <FormattedMessage
          id="auth.registerMember.passwordStep.description"
          defaultMessage="Set your password to protect your account and data"
          description="Brief description of use of setting password"
        />
      </p>
      <FormItem
        label={intl.formatMessage({
          id: "auth.registerMember.passwordStep.password",
          defaultMessage: "Set Password",
          description: "Title of the password field on the register form",
        })}
        name={SignupField.PASSWORD}
        requiredMark="optional"
        rules={passwordFieldValidationRules}
      >
        <Input.Password autoComplete="new-password" size="large" spellCheck="false" />
      </FormItem>
      <FormItem
        dependencies={[SignupField.PASSWORD]}
        label={intl.formatMessage({
          id: "auth.registerMember.passwordStep.confirmPassword",
          defaultMessage: "Confirm Password",
          description: "Title of the confirm Password field on the register form",
        })}
        name={SignupField.PASSWORD_CONFIRMATION}
        requiredMark="optional"
        rules={confirmPasswordFieldValidationRules}
      >
        <Input.Password autoComplete="new-password" size="large" spellCheck="false" />
      </FormItem>
      <FormItem>
        <PasswordValidation validationResult={passwordValidationResult} />
      </FormItem>
      <FormItem
        className={styles.tncField}
        name={SignupField.AGREE_WITH_TNC}
        rules={tncValidation}
        valuePropName="checked"
      >
        <Checkbox>
          <FormattedMessage
            id="auth.registerMember.orgDetailsStep.tncLinkTitle"
            defaultMessage="I agree to the <link>Terms & Conditions</link>"
            description="Title of the tnc Link"
            values={{
              link: (linkText: ReactNode) => (
                <a
                  href="https://mapmycustomers.com/terms/"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {linkText}
                </a>
              ),
            }}
          />
        </Checkbox>
      </FormItem>
      <FormItem name={SignupField.PROMO_EMAILS} valuePropName="checked">
        <Checkbox>
          <FormattedMessage
            id="auth.registerMember.orgDetailsStep.promoEmails"
            defaultMessage="I’d like to receive promotional emails {tooltipIcon}"
            description="Title of the promotional email checkbox on the 3rd step of Registration page"
            values={{
              tooltipIcon: (
                <Tooltip
                  placement="topLeft"
                  title={intl.formatMessage({
                    id: "auth.registerMember.orgDetailsStep.promoEmails.tooltip",
                    defaultMessage:
                      "Select to receive promotional emails from Map My Customers about app updates, improvements and other content that may be of interest to me",
                    description:
                      "Tooltip for the promotional email checkbox on the 3rd step of Registration page",
                  })}
                >
                  <FontAwesomeIcon className={styles.tooltipIcon} icon={faInfoCircle} />
                </Tooltip>
              ),
            }}
          />
        </Checkbox>
      </FormItem>
      <FormItem>
        <div className={cn(styles.buttons, styles.verticalButtons)}>
          <Button
            className={styles.submitButton}
            disabled={!isValid || signingUp}
            htmlType="submit"
            loading={signingUp}
            type="primary"
          >
            {signingUp
              ? intl.formatMessage({
                  id: "auth.register.submitButton.loading",
                  defaultMessage: "Signing up...",
                  description:
                    "Title of the submiting current details of first step of the register form",
                })
              : intl.formatMessage({
                  id: "auth.register.submitButton.register",
                  defaultMessage: "Let's dive in!",
                  description: "Title of the register button for third step of the register form",
                })}
          </Button>
          <Button htmlType="button" onClick={onBackClick} type="link">
            <FormattedMessage
              id="auth.register.previousStepButton"
              defaultMessage="Go back"
              description="Title of the Go Back button for the Register Form"
            />
          </Button>
        </div>
      </FormItem>
    </div>
  );
};

export default PasswordStep;
